import { html, svg, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LitPage } from '../lit-page';
import { devWarn, isPuzzleId, type PuzzleId } from '../luna-common';

@customElement('luna-board-art')
export class LunaBoardArt extends LitPage {
  private _puzzle: PuzzleId = 'map';

  @property({ attribute: 'puzzle' })
  get puzzle(): PuzzleId {
    return this._puzzle;
  }

  set puzzle(value: string | null) {
    const next = String(value ?? '');
    if (!isPuzzleId(next)) {
      devWarn(`luna-board-art: unknown puzzle "${next}" — falling back to "map"`);
      this._puzzle = 'map';
      return;
    }
    this._puzzle = next;
  }

  private _size = 52;

  @property({ type: Number })
  get size(): number {
    return this._size;
  }

  set size(value: number | null) {
    const next = Number(value);
    if (!Number.isFinite(next) || next <= 0) {
      devWarn(`luna-board-art: invalid size "${value}" — falling back to 52`);
      this._size = 52;
      return;
    }
    this._size = next;
  }

  content(): TemplateResult<1> {
    const size = this._size;
    return svg`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width=${size}
        height=${size}
        viewBox="0 0 120 120"
        fill="none"
        role="img"
        aria-label=${this._puzzle}
      >
        ${this.art()}
      </svg>
    ` as unknown as TemplateResult<1>;
  }

  // Illustrations derive from the Figma "Board Mock" component set (nodes
  // 21:2 … 21:142) — except Map, which is drawn from real gameplay boards
  // (polyomino regions, chamfered corners). Intentional deviations from the
  // export, per design review: Pegs is re-gridded onto an even 24px diamond
  // lattice so the pegs never overlap. Rectangles follows the 21:49 variant
  // (5×5 grid, shaded regions, numbered cells); its numbers are IBM Plex
  // Mono SemiBold — Figma says Bold, but 600 is the heaviest self-hosted
  // mono weight.
  private art() {
    switch (this._puzzle) {
      case 'map':
        // Six regions as gameplay-style polyominoes on a 5×5 grid of 24px
        // cells (real Map boards, not the Figma export): region strokes are
        // centred on shared boundaries so they overlap into single 2px lines
        // (same trick as Rectangles), with 8px 45° chamfers at every interior
        // corner. Where a border runs straight into a T-junction it jogs
        // through the two corner chamfers, so no background gap opens up.
        return svg`
          <g stroke="white" stroke-width="2">
            <path d="M0 0L72 0L72 16L64 24L56 24L48 32L48 40L40 48L32 48L24 40L24 32L16 24L0 24Z" fill="#177245"/>
            <path d="M72 0L120 0L120 24L104 24L96 32L96 40L88 48L80 48L72 56L72 64L64 72L56 72L48 64L48 56L40 48L48 40L48 32L56 24L64 24L72 16Z" fill="#0067B3"/>
            <path d="M104 24L120 24L120 72L104 72L96 80L88 72L80 72L72 64L72 56L80 48L88 48L96 40L96 32Z" fill="#B07F00"/>
            <path d="M0 24L16 24L24 32L24 40L32 48L40 48L48 56L48 64L40 72L32 72L24 80L24 88L32 96L40 96L48 104L48 120L0 120Z" fill="#C74A24"/>
            <path d="M32 72L40 72L48 64L56 72L64 72L72 64L80 72L88 72L96 80L96 88L88 96L80 96L72 104L72 120L48 120L48 104L40 96L32 96L24 88L24 80Z" fill="#177245"/>
            <path d="M104 72L120 72L120 120L72 120L72 104L80 96L88 96L96 88L96 80Z" fill="#0067B3"/>
          </g>
        `;
      case 'pegs':
        return svg`
          <g fill="#C74A24">
            <circle cx="60" cy="12" r="9"/>
            <circle cx="36" cy="36" r="9"/>
            <circle cx="60" cy="36" r="9"/>
            <circle cx="84" cy="36" r="9"/>
            <circle cx="12" cy="60" r="9"/>
            <circle cx="36" cy="60" r="9"/>
            <circle cx="84" cy="60" r="9"/>
            <circle cx="108" cy="60" r="9"/>
            <circle cx="36" cy="84" r="9"/>
            <circle cx="60" cy="84" r="9"/>
            <circle cx="84" cy="84" r="9"/>
            <circle cx="60" cy="108" r="9"/>
          </g>
          <circle cx="60" cy="60" r="9" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.5"/>
          <g fill="white" fill-opacity="0.55">
            <circle cx="57" cy="9" r="3"/>
            <circle cx="33" cy="33" r="3"/>
            <circle cx="57" cy="33" r="3"/>
            <circle cx="81" cy="33" r="3"/>
            <circle cx="9" cy="57" r="3"/>
            <circle cx="33" cy="57" r="3"/>
            <circle cx="81" cy="57" r="3"/>
            <circle cx="105" cy="57" r="3"/>
            <circle cx="33" cy="81" r="3"/>
            <circle cx="57" cy="81" r="3"/>
            <circle cx="81" cy="81" r="3"/>
            <circle cx="57" cy="105" r="3"/>
          </g>
        `;
      case 'rectangles':
        return svg`
          <path d="M0 0H48V24H0V0Z" fill="#C7D0DC"/>
          <path d="M24 24H72V72H24V24Z" fill="#C7D0DC"/>
          <path d="M48 72H72V120H48V72Z" fill="#C7D0DC"/>
          <path d="M72 96H120V120H72V96Z" fill="#C7D0DC"/>
          <path d="M24 0V120M48 0V120M72 0V120M96 0V120M0 24H120M0 48H120M0 72H120M0 96H120" stroke="#CCD7EA" stroke-width="1"/>
          <path d="M1 1H119V119H1V1Z" stroke="#002E51" stroke-width="3"/>
          <!-- Region strokes are centred on their boundaries (as exported), so
               edges shared by two regions overlap into a single 2px line. -->
          <g stroke="#002E51" stroke-width="2" stroke-linejoin="round">
            <path d="M0 0H48V24H0V0Z"/>
            <path d="M48 0H120V24H48V0Z"/>
            <path d="M0 24H24V72H0V24Z"/>
            <path d="M24 24H72V72H24V24Z"/>
            <path d="M72 24H120V72H72V24Z"/>
            <path d="M0 72H48V120H0V72Z"/>
            <path d="M48 72H72V120H48V72Z"/>
            <path d="M72 72H120V96H72V72Z"/>
            <path d="M72 96H120V120H72V96Z"/>
          </g>
          <g font-family="'IBM Plex Mono', monospace" font-size="14" font-weight="600" fill="#002E51" text-anchor="middle" dominant-baseline="central">
            <text x="12" y="12">2</text>
            <text x="60" y="12">3</text>
            <text x="12" y="36">2</text>
            <text x="36" y="36">4</text>
            <text x="84" y="37">4</text>
            <text x="12" y="84">4</text>
            <text x="60" y="84">2</text>
            <text x="84" y="84">2</text>
            <text x="84" y="108">2</text>
          </g>
        `;
      case 'tents':
        return svg`
          <path d="M23 10H13C11.3431 10 10 11.3431 10 13V23C10 24.6569 11.3431 26 13 26H23C24.6569 26 26 24.6569 26 23V13C26 11.3431 24.6569 10 23 10Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M23 33H13C11.3431 33 10 34.3431 10 36V46C10 47.6569 11.3431 49 13 49H23C24.6569 49 26 47.6569 26 46V36C26 34.3431 24.6569 33 23 33Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M23 56H13C11.3431 56 10 57.3431 10 59V69C10 70.6569 11.3431 72 13 72H23C24.6569 72 26 70.6569 26 69V59C26 57.3431 24.6569 56 23 56Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M23 79H13C11.3431 79 10 80.3431 10 82V92C10 93.6569 11.3431 95 13 95H23C24.6569 95 26 93.6569 26 92V82C26 80.3431 24.6569 79 23 79Z" fill="#E9EFE3"/>
          <path d="M18 90C20.7614 90 23 87.7614 23 85C23 82.2386 20.7614 80 18 80C15.2386 80 13 82.2386 13 85C13 87.7614 15.2386 90 18 90Z" fill="#5A7D3F"/>
          <path d="M19 87H17V93H19V87Z" fill="#5C4326"/>
          <path d="M23 102H13C11.3431 102 10 103.343 10 105V115C10 116.657 11.3431 118 13 118H23C24.6569 118 26 116.657 26 115V105C26 103.343 24.6569 102 23 102Z" fill="#B07F00"/>
          <path d="M13 115L18 105L23 115H13Z" fill="white"/>
          <path d="M46 10H36C34.3431 10 33 11.3431 33 13V23C33 24.6569 34.3431 26 36 26H46C47.6569 26 49 24.6569 49 23V13C49 11.3431 47.6569 10 46 10Z" fill="#E9EFE3"/>
          <path d="M41 21C43.7614 21 46 18.7614 46 16C46 13.2386 43.7614 11 41 11C38.2386 11 36 13.2386 36 16C36 18.7614 38.2386 21 41 21Z" fill="#5A7D3F"/>
          <path d="M42 18H40V24H42V18Z" fill="#5C4326"/>
          <path d="M46 33H36C34.3431 33 33 34.3431 33 36V46C33 47.6569 34.3431 49 36 49H46C47.6569 49 49 47.6569 49 46V36C49 34.3431 47.6569 33 46 33Z" fill="#B07F00"/>
          <path d="M36 46L41 36L46 46H36Z" fill="white"/>
          <path d="M46 56H36C34.3431 56 33 57.3431 33 59V69C33 70.6569 34.3431 72 36 72H46C47.6569 72 49 70.6569 49 69V59C49 57.3431 47.6569 56 46 56Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M46 79H36C34.3431 79 33 80.3431 33 82V92C33 93.6569 34.3431 95 36 95H46C47.6569 95 49 93.6569 49 92V82C49 80.3431 47.6569 79 46 79Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M46 102H36C34.3431 102 33 103.343 33 105V115C33 116.657 34.3431 118 36 118H46C47.6569 118 49 116.657 49 115V105C49 103.343 47.6569 102 46 102Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M69 10H59C57.3431 10 56 11.3431 56 13V23C56 24.6569 57.3431 26 59 26H69C70.6569 26 72 24.6569 72 23V13C72 11.3431 70.6569 10 69 10Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M69 33H59C57.3431 33 56 34.3431 56 36V46C56 47.6569 57.3431 49 59 49H69C70.6569 49 72 47.6569 72 46V36C72 34.3431 70.6569 33 69 33Z" fill="#B07F00"/>
          <path d="M59 46L64 36L69 46H59Z" fill="white"/>
          <path d="M69 56H59C57.3431 56 56 57.3431 56 59V69C56 70.6569 57.3431 72 59 72H69C70.6569 72 72 70.6569 72 69V59C72 57.3431 70.6569 56 69 56Z" fill="#E9EFE3"/>
          <path d="M64 67C66.7614 67 69 64.7614 69 62C69 59.2386 66.7614 57 64 57C61.2386 57 59 59.2386 59 62C59 64.7614 61.2386 67 64 67Z" fill="#5A7D3F"/>
          <path d="M65 64H63V70H65V64Z" fill="#5C4326"/>
          <path d="M69 79H59C57.3431 79 56 80.3431 56 82V92C56 93.6569 57.3431 95 59 95H69C70.6569 95 72 93.6569 72 92V82C72 80.3431 70.6569 79 69 79Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M69 102H59C57.3431 102 56 103.343 56 105V115C56 116.657 57.3431 118 59 118H69C70.6569 118 72 116.657 72 115V105C72 103.343 70.6569 102 69 102Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M92 10H82C80.3431 10 79 11.3431 79 13V23C79 24.6569 80.3431 26 82 26H92C93.6569 26 95 24.6569 95 23V13C95 11.3431 93.6569 10 92 10Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M92 33H82C80.3431 33 79 34.3431 79 36V46C79 47.6569 80.3431 49 82 49H92C93.6569 49 95 47.6569 95 46V36C95 34.3431 93.6569 33 92 33Z" fill="#E9EFE3"/>
          <path d="M87 44C89.7614 44 92 41.7614 92 39C92 36.2386 89.7614 34 87 34C84.2386 34 82 36.2386 82 39C82 41.7614 84.2386 44 87 44Z" fill="#5A7D3F"/>
          <path d="M88 41H86V47H88V41Z" fill="#5C4326"/>
          <path d="M92 56H82C80.3431 56 79 57.3431 79 59V69C79 70.6569 80.3431 72 82 72H92C93.6569 72 95 70.6569 95 69V59C95 57.3431 93.6569 56 92 56Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M92 79H82C80.3431 79 79 80.3431 79 82V92C79 93.6569 80.3431 95 82 95H92C93.6569 95 95 93.6569 95 92V82C95 80.3431 93.6569 79 92 79Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M92 102H82C80.3431 102 79 103.343 79 105V115C79 116.657 80.3431 118 82 118H92C93.6569 118 95 116.657 95 115V105C95 103.343 93.6569 102 92 102Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M115 10H105C103.343 10 102 11.3431 102 13V23C102 24.6569 103.343 26 105 26H115C116.657 26 118 24.6569 118 23V13C118 11.3431 116.657 10 115 10Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M115 33H105C103.343 33 102 34.3431 102 36V46C102 47.6569 103.343 49 105 49H115C116.657 49 118 47.6569 118 46V36C118 34.3431 116.657 33 115 33Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M115 56H105C103.343 56 102 57.3431 102 59V69C102 70.6569 103.343 72 105 72H115C116.657 72 118 70.6569 118 69V59C118 57.3431 116.657 56 115 56Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M115 79H105C103.343 79 102 80.3431 102 82V92C102 93.6569 103.343 95 105 95H115C116.657 95 118 93.6569 118 92V82C118 80.3431 116.657 79 115 79Z" fill="white" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M115 102H105C103.343 102 102 103.343 102 105V115C102 116.657 103.343 118 105 118H115C116.657 118 118 116.657 118 115V105C118 103.343 116.657 102 115 102Z" fill="#E9EFE3"/>
          <path d="M110 113C112.761 113 115 110.761 115 108C115 105.239 112.761 103 110 103C107.239 103 105 105.239 105 108C105 110.761 107.239 113 110 113Z" fill="#5A7D3F"/>
          <path d="M111 110H109V116H111V110Z" fill="#5C4326"/>
        `;
      case 'unruly':
        return svg`
          <path d="M25 12H15C13.3431 12 12 13.3431 12 15V25C12 26.6569 13.3431 28 15 28H25C26.6569 28 28 26.6569 28 25V15C28 13.3431 26.6569 12 25 12Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M25 33H15C13.3431 33 12 34.3431 12 36V46C12 47.6569 13.3431 49 15 49H25C26.6569 49 28 47.6569 28 46V36C28 34.3431 26.6569 33 25 33Z" fill="white" stroke="#9B4C8A" stroke-width="2"/>
          <path d="M25 54H15C13.3431 54 12 55.3431 12 57V67C12 68.6569 13.3431 70 15 70H25C26.6569 70 28 68.6569 28 67V57C28 55.3431 26.6569 54 25 54Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M25 75H15C13.3431 75 12 76.3431 12 78V88C12 89.6569 13.3431 91 15 91H25C26.6569 91 28 89.6569 28 88V78C28 76.3431 26.6569 75 25 75Z" fill="#9B4C8A"/>
          <path d="M25 96H15C13.3431 96 12 97.3431 12 99V109C12 110.657 13.3431 112 15 112H25C26.6569 112 28 110.657 28 109V99C28 97.3431 26.6569 96 25 96Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M46 12H36C34.3431 12 33 13.3431 33 15V25C33 26.6569 34.3431 28 36 28H46C47.6569 28 49 26.6569 49 25V15C49 13.3431 47.6569 12 46 12Z" fill="#9B4C8A"/>
          <path d="M46 33H36C34.3431 33 33 34.3431 33 36V46C33 47.6569 34.3431 49 36 49H46C47.6569 49 49 47.6569 49 46V36C49 34.3431 47.6569 33 46 33Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M46 54H36C34.3431 54 33 55.3431 33 57V67C33 68.6569 34.3431 70 36 70H46C47.6569 70 49 68.6569 49 67V57C49 55.3431 47.6569 54 46 54Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M46 75H36C34.3431 75 33 76.3431 33 78V88C33 89.6569 34.3431 91 36 91H46C47.6569 91 49 89.6569 49 88V78C49 76.3431 47.6569 75 46 75Z" fill="white" stroke="#9B4C8A" stroke-width="2"/>
          <path d="M46 96H36C34.3431 96 33 97.3431 33 99V109C33 110.657 34.3431 112 36 112H46C47.6569 112 49 110.657 49 109V99C49 97.3431 47.6569 96 46 96Z" fill="#9B4C8A"/>
          <path d="M67 12H57C55.3431 12 54 13.3431 54 15V25C54 26.6569 55.3431 28 57 28H67C68.6569 28 70 26.6569 70 25V15C70 13.3431 68.6569 12 67 12Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M67 33H57C55.3431 33 54 34.3431 54 36V46C54 47.6569 55.3431 49 57 49H67C68.6569 49 70 47.6569 70 46V36C70 34.3431 68.6569 33 67 33Z" fill="#9B4C8A"/>
          <path d="M67 54H57C55.3431 54 54 55.3431 54 57V67C54 68.6569 55.3431 70 57 70H67C68.6569 70 70 68.6569 70 67V57C70 55.3431 68.6569 54 67 54Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M67 75H57C55.3431 75 54 76.3431 54 78V88C54 89.6569 55.3431 91 57 91H67C68.6569 91 70 89.6569 70 88V78C70 76.3431 68.6569 75 67 75Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M67 96H57C55.3431 96 54 97.3431 54 99V109C54 110.657 55.3431 112 57 112H67C68.6569 112 70 110.657 70 109V99C70 97.3431 68.6569 96 67 96Z" fill="white" stroke="#9B4C8A" stroke-width="2"/>
          <path d="M88 12H78C76.3431 12 75 13.3431 75 15V25C75 26.6569 76.3431 28 78 28H88C89.6569 28 91 26.6569 91 25V15C91 13.3431 89.6569 12 88 12Z" fill="white" stroke="#9B4C8A" stroke-width="2"/>
          <path d="M88 33H78C76.3431 33 75 34.3431 75 36V46C75 47.6569 76.3431 49 78 49H88C89.6569 49 91 47.6569 91 46V36C91 34.3431 89.6569 33 88 33Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M88 54H78C76.3431 54 75 55.3431 75 57V67C75 68.6569 76.3431 70 78 70H88C89.6569 70 91 68.6569 91 67V57C91 55.3431 89.6569 54 88 54Z" fill="#9B4C8A"/>
          <path d="M88 75H78C76.3431 75 75 76.3431 75 78V88C75 89.6569 76.3431 91 78 91H88C89.6569 91 91 89.6569 91 88V78C91 76.3431 89.6569 75 88 75Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M88 96H78C76.3431 96 75 97.3431 75 99V109C75 110.657 76.3431 112 78 112H88C89.6569 112 91 110.657 91 109V99C91 97.3431 89.6569 96 88 96Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M109 12H99C97.3431 12 96 13.3431 96 15V25C96 26.6569 97.3431 28 99 28H109C110.657 28 112 26.6569 112 25V15C112 13.3431 110.657 12 109 12Z" fill="#9B4C8A"/>
          <path d="M109 33H99C97.3431 33 96 34.3431 96 36V46C96 47.6569 97.3431 49 99 49H109C110.657 49 112 47.6569 112 46V36C112 34.3431 110.657 33 109 33Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M109 54H99C97.3431 54 96 55.3431 96 57V67C96 68.6569 97.3431 70 99 70H109C110.657 70 112 68.6569 112 67V57C112 55.3431 110.657 54 109 54Z" fill="white" stroke="#9B4C8A" stroke-width="2"/>
          <path d="M109 75H99C97.3431 75 96 76.3431 96 78V88C96 89.6569 97.3431 91 99 91H109C110.657 91 112 89.6569 112 88V78C112 76.3431 110.657 75 109 75Z" fill="#EEF2F8" stroke="#CCD7EA" stroke-width="1.2"/>
          <path d="M109 96H99C97.3431 96 96 97.3431 96 99V109C96 110.657 97.3431 112 99 112H109C110.657 112 112 110.657 112 109V99C112 97.3431 110.657 96 109 96Z" fill="#9B4C8A"/>
        `;
      case 'untangle':
        return svg`
          <path d="M30 28L70 60" stroke="#CCD7EA" stroke-width="3" stroke-linecap="round"/>
          <path d="M95 40L20 80" stroke="#CCD7EA" stroke-width="3" stroke-linecap="round"/>
          <path d="M95 40L70 60" stroke="#CCD7EA" stroke-width="3" stroke-linecap="round"/>
          <path d="M30 28L92 95" stroke="#3785D4" stroke-width="4" stroke-linecap="round" stroke-dasharray="1 7"/>
          <path d="M30 37C34.9706 37 39 32.9706 39 28C39 23.0294 34.9706 19 30 19C25.0294 19 21 23.0294 21 28C21 32.9706 25.0294 37 30 37Z" fill="#3785D4" stroke="white" stroke-width="2.5"/>
          <path d="M95 49C99.9706 49 104 44.9706 104 40C104 35.0294 99.9706 31 95 31C90.0294 31 86 35.0294 86 40C86 44.9706 90.0294 49 95 49Z" fill="#3785D4" stroke="white" stroke-width="2.5"/>
          <path d="M20 89C24.9706 89 29 84.9706 29 80C29 75.0294 24.9706 71 20 71C15.0294 71 11 75.0294 11 80C11 84.9706 15.0294 89 20 89Z" fill="#3785D4" stroke="white" stroke-width="2.5"/>
          <path d="M70 69C74.9706 69 79 64.9706 79 60C79 55.0294 74.9706 51 70 51C65.0294 51 61 55.0294 61 60C61 64.9706 65.0294 69 70 69Z" fill="#3785D4" stroke="white" stroke-width="2.5"/>
          <path d="M92 104C96.9706 104 101 99.9706 101 95C101 90.0294 96.9706 86 92 86C87.0294 86 83 90.0294 83 95C83 99.9706 87.0294 104 92 104Z" fill="#3785D4" stroke="white" stroke-width="2.5"/>
        `;
      default:
        return html``;
    }
  }
}
